(() => {
  const SELECTOR = '[data-product-root]';

  function bestSource(img) {
    const candidates = (img.getAttribute('srcset') || '')
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => {
        const bits = part.split(/\s+/);
        const width = Number((bits[1] || '').replace('w', '')) || 0;
        return { url: bits[0], width };
      })
      .filter((item) => item.url);

    if (candidates.length) {
      candidates.sort((a, b) => b.width - a.width);
      return candidates[0].url;
    }
    return img.currentSrc || img.src;
  }

  function icon(name) {
    if (name === 'close') return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19"/></svg>';
    if (name === 'prev') return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg>';
    if (name === 'next') return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7"/></svg>';
    if (name === 'zoom') return '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.5" cy="10.5" r="5.5"/><path d="M15 15l5 5M10.5 8v5M8 10.5h5"/></svg>';
    return '';
  }

  function setupViewer(root) {
    if (!root || root.dataset.meridianViewerReady === 'true') return;

    const figures = [...root.querySelectorAll('.dlf-pdp__media')]
      .filter((figure) => figure.querySelector('img.dlf-pdp__image'));
    if (!figures.length) return;

    const items = figures.map((figure, index) => {
      const img = figure.querySelector('img.dlf-pdp__image');
      figure.classList.add('dlf-pdp__media--zoomable');
      figure.dataset.viewerIndex = String(index);
      figure.tabIndex = 0;
      figure.setAttribute('role', 'button');
      figure.setAttribute('aria-label', `Open image ${index + 1} of ${figures.length}`);

      if (!figure.querySelector('.dlf-pdp__zoom-cue')) {
        const cue = document.createElement('span');
        cue.className = 'dlf-pdp__zoom-cue';
        cue.setAttribute('aria-hidden', 'true');
        cue.innerHTML = icon('zoom');
        figure.appendChild(cue);
      }

      return { figure, img };
    });

    const viewer = document.createElement('div');
    viewer.className = 'dlf-pdp-lightbox';
    viewer.hidden = true;
    viewer.setAttribute('aria-hidden', 'true');
    viewer.setAttribute('role', 'dialog');
    viewer.setAttribute('aria-modal', 'true');
    viewer.setAttribute('aria-label', 'Product image viewer');
    viewer.innerHTML = `
      <div class="dlf-pdp-lightbox__toolbar">
        <button type="button" class="dlf-pdp-lightbox__tool" data-viewer-zoom aria-label="Zoom image">${icon('zoom')}</button>
        <button type="button" class="dlf-pdp-lightbox__tool" data-viewer-close aria-label="Close image viewer">${icon('close')}</button>
      </div>
      <button type="button" class="dlf-pdp-lightbox__nav dlf-pdp-lightbox__nav--prev" data-viewer-prev aria-label="Previous image">${icon('prev')}</button>
      <div class="dlf-pdp-lightbox__viewport" data-viewer-viewport>
        <img class="dlf-pdp-lightbox__image" data-viewer-image alt="">
      </div>
      <button type="button" class="dlf-pdp-lightbox__nav dlf-pdp-lightbox__nav--next" data-viewer-next aria-label="Next image">${icon('next')}</button>
      <div class="dlf-pdp-lightbox__footer">
        <span data-viewer-count></span>
        <span data-viewer-hint>Click image to zoom</span>
      </div>`;
    document.body.appendChild(viewer);

    const image = viewer.querySelector('[data-viewer-image]');
    const viewport = viewer.querySelector('[data-viewer-viewport]');
    const count = viewer.querySelector('[data-viewer-count]');
    const hint = viewer.querySelector('[data-viewer-hint]');
    const closeButton = viewer.querySelector('[data-viewer-close]');
    const zoomButton = viewer.querySelector('[data-viewer-zoom]');
    const prevButton = viewer.querySelector('[data-viewer-prev]');
    const nextButton = viewer.querySelector('[data-viewer-next]');

    let index = 0;
    let zoom = 1;
    let panX = 0;
    let panY = 0;
    let drag = null;
    let previousFocus = null;

    const resetTransform = () => {
      zoom = 1;
      panX = 0;
      panY = 0;
      image.style.transform = '';
      viewer.classList.remove('is-zoomed');
      zoomButton.setAttribute('aria-label', 'Zoom image');
      if (hint) hint.textContent = 'Click image to zoom';
    };

    const clampPan = () => {
      const maxX = Math.max(0, (image.offsetWidth * (zoom - 1)) / 2);
      const maxY = Math.max(0, (image.offsetHeight * (zoom - 1)) / 2);
      panX = Math.max(-maxX, Math.min(maxX, panX));
      panY = Math.max(-maxY, Math.min(maxY, panY));
    };

    const applyTransform = () => {
      clampPan();
      image.style.transform = `translate3d(${panX}px, ${panY}px, 0) scale(${zoom})`;
    };

    const setZoom = (nextZoom) => {
      zoom = nextZoom > 1 ? 2 : 1;
      if (zoom === 1) {
        resetTransform();
        return;
      }
      panX = 0;
      panY = 0;
      viewer.classList.add('is-zoomed');
      zoomButton.setAttribute('aria-label', 'Reset zoom');
      if (hint) hint.textContent = 'Drag to inspect · click to reset';
      applyTransform();
    };

    const render = () => {
      resetTransform();
      const item = items[index];
      image.alt = item.img.alt || '';
      image.src = bestSource(item.img);
      count.textContent = `${index + 1} / ${items.length}`;
      const multiple = items.length > 1;
      prevButton.hidden = !multiple;
      nextButton.hidden = !multiple;
    };

    const open = (nextIndex) => {
      index = (nextIndex + items.length) % items.length;
      previousFocus = document.activeElement;
      render();
      viewer.hidden = false;
      viewer.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('dlf-pdp-viewer-open');
      closeButton.focus({ preventScroll: true });
    };

    const close = () => {
      if (viewer.hidden) return;
      viewer.hidden = true;
      viewer.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('dlf-pdp-viewer-open');
      resetTransform();
      if (previousFocus && typeof previousFocus.focus === 'function') previousFocus.focus({ preventScroll: true });
    };

    const move = (delta) => {
      index = (index + delta + items.length) % items.length;
      render();
    };

    items.forEach((item, itemIndex) => {
      item.figure.addEventListener('click', (event) => {
        if (event.target.closest('a,button')) return;
        open(itemIndex);
      });
      item.figure.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          open(itemIndex);
        }
      });
    });

    closeButton.addEventListener('click', close);
    prevButton.addEventListener('click', () => move(-1));
    nextButton.addEventListener('click', () => move(1));
    zoomButton.addEventListener('click', () => setZoom(zoom === 1 ? 2 : 1));
    image.addEventListener('click', (event) => {
      if (drag?.moved) return;
      event.stopPropagation();
      setZoom(zoom === 1 ? 2 : 1);
    });

    viewer.addEventListener('click', (event) => {
      if (event.target === viewer || event.target === viewport) close();
    });

    image.addEventListener('pointerdown', (event) => {
      if (zoom === 1) return;
      event.preventDefault();
      image.setPointerCapture?.(event.pointerId);
      drag = { id: event.pointerId, x: event.clientX, y: event.clientY, panX, panY, moved: false };
      viewer.classList.add('is-dragging');
    });

    image.addEventListener('pointermove', (event) => {
      if (!drag || drag.id !== event.pointerId) return;
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      if (Math.abs(dx) + Math.abs(dy) > 4) drag.moved = true;
      panX = drag.panX + dx;
      panY = drag.panY + dy;
      applyTransform();
    });

    const endDrag = (event) => {
      if (!drag || drag.id !== event.pointerId) return;
      viewer.classList.remove('is-dragging');
      window.setTimeout(() => { drag = null; }, 0);
    };
    image.addEventListener('pointerup', endDrag);
    image.addEventListener('pointercancel', endDrag);

    document.addEventListener('keydown', (event) => {
      if (viewer.hidden) return;
      if (event.key === 'Escape') close();
      else if (event.key === 'ArrowLeft') move(-1);
      else if (event.key === 'ArrowRight') move(1);
      else if (event.key === '+' || event.key === '=') setZoom(2);
      else if (event.key === '-') setZoom(1);
    });

    root.dataset.meridianViewerReady = 'true';
  }

  function init(scope = document) {
    scope.querySelectorAll?.(SELECTOR).forEach(setupViewer);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
